import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AppDialog } from "./AppDialog";
import { DialogQueue, type ConfirmOptions, type PendingDialog } from "./dialogQueue";

export type { ConfirmOptions };

type DialogContextValue = {
  /** Pose une question. Résout `true` si confirmé, `false` sinon. */
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  /** Informe (un seul bouton). Résout quand l'utilisateur a pris connaissance. */
  alert:   (opts: Omit<ConfirmOptions, "alertOnly" | "cancelLabel">) => Promise<void>;
};

const DialogContext = createContext<DialogContextValue | null>(null);

/**
 * Fournit `useDialog()` — dialogues applicatifs en remplacement d'`Alert.alert`.
 *
 * ## Pourquoi un provider plutôt qu'un état par écran
 * Une décision utilisateur est SÉQUENTIELLE : on pose une question, on attend la
 * réponse, on agit. Un composant déclaratif imposerait à chaque écran de porter
 * un état + un JSX + un handler, soit la même plomberie dupliquée sur 12 écrans.
 * Ici l'appelant écrit simplement :
 *
 *     if (await confirm({ title: "Annuler la course ?", danger: true })) { … }
 *
 * ## Deux écueils traités explicitement
 * 1. **Demandes concurrentes** — deux `confirm()` quasi simultanés (double tap,
 *    deux effets) : on met en FILE au lieu d'écraser le premier, sinon sa
 *    promesse ne se résoudrait jamais et le code appelant resterait bloqué.
 * 2. **Démontage pendant l'attente** — navigation pendant qu'un dialogue est
 *    ouvert : au démontage on résout toutes les promesses en attente à `false`
 *    (refus par défaut, jamais une confirmation accidentelle).
 */
export function DialogProvider({ children }: { children: ReactNode }) {
  // La file vit dans une ref (logique pure, testée dans dialogQueue.test.ts) ;
  // `current` n'existe que pour déclencher le rendu.
  const queue = useRef(new DialogQueue());
  const [current, setCurrent] = useState<PendingDialog | null>(null);

  useEffect(() => {
    const q = queue.current;
    return () => {
      // Démontage : aucune promesse ne doit rester pendante (écran figé).
      q.clear();
    };
  }, []);

  // Ferme le dialogue courant avec une réponse, puis affiche le suivant.
  const settle = useCallback((answer: boolean) => {
    setCurrent(queue.current.settle(answer));
  }, []);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      queue.current.push({ opts, resolve });
      setCurrent(queue.current.getCurrent());
    });
  }, []);

  const alert = useCallback(
    async (opts: Omit<ConfirmOptions, "alertOnly" | "cancelLabel">): Promise<void> => {
      await confirm({ ...opts, alertOnly: true });
    },
    [confirm],
  );

  // Expose l'API aux appelants hors React (services, BackHandler). Même patron
  // que `bindImperativeToast`.
  useEffect(() => {
    bindImperativeDialog({ confirm, alert });
  }, [confirm, alert]);

  const opts = current?.opts;

  return (
    <DialogContext.Provider value={{ confirm, alert }}>
      {children}

      {opts && (
        <AppDialog
          visible
          tone={opts.tone ?? (opts.danger ? "danger" : "info")}
          title={opts.title}
          message={opts.message}
          details={opts.details}
          dismissOnBackdrop={!opts.blocking}
          onClose={() => settle(false)}
          confirm={{
            label:   opts.confirmLabel ?? (opts.alertOnly ? "OK" : "Confirmer"),
            onPress: () => settle(true),
            danger:  opts.danger,
          }}
          // Un dialogue informatif n'a qu'une seule issue.
          cancel={
            opts.alertOnly
              ? undefined
              : { label: opts.cancelLabel ?? "Annuler", onPress: () => settle(false) }
          }
        />
      )}
    </DialogContext.Provider>
  );
}

/**
 * Accès aux dialogues applicatifs.
 *
 *     const { confirm, alert } = useDialog();
 *     if (await confirm({ title: "Annuler la course ?", danger: true })) { … }
 *     await alert({ title: "Course créée", tone: "success" });
 */
export function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog() doit être utilisé sous <DialogProvider>.");
  }
  return ctx;
}

// ─── API impérative (hors React) ──────────────────────────────────────────────
// Même patron que `toast` : certains appelants ne sont PAS des composants et ne
// peuvent donc pas utiliser le hook — services (cameraService), handlers hors composant
// (useExitConfirm). La référence est posée par le provider
// au montage.
let imperativeRef: DialogContextValue | null = null;

export function bindImperativeDialog(ctx: DialogContextValue): void {
  imperativeRef = ctx;
}

export const dialog = {
  /**
   * Question hors composant. Si le provider n'est pas monté (cas théorique :
   * appel avant le premier rendu), on résout `false` — refus par défaut, jamais
   * une confirmation accidentelle.
   */
  confirm: (opts: ConfirmOptions): Promise<boolean> =>
    imperativeRef?.confirm(opts) ?? Promise.resolve(false),
  alert: (opts: Omit<ConfirmOptions, "alertOnly" | "cancelLabel">): Promise<void> =>
    imperativeRef?.alert(opts) ?? Promise.resolve(),
};
